import { useState, useEffect, useCallback, useMemo } from 'react';
import { Building2, User, Star, X, Plus, Loader2 } from 'lucide-react';
import type { ClientRelationship } from '../../lib/types';
import { useContactCompanyLinkStore } from '../../lib/stores/contactCompanyLinkStore';
import { ContactCompanyLinkPicker } from './ContactCompanyLinkPicker';
import { RelationshipLabelPicker } from './RelationshipLabelPicker';
import { useEntityDrawer } from '../../contexts/EntityDrawerContext';
import { extractCompanyDomain, findCompanyByDomain, type DomainCompanyMatch } from '../../lib/utils/companyDomainMatch';
import { getCurrentUserOrganizationId } from '../../lib/stores/referenceDataCache';

interface Props {
  clientId: string;
  /** 'person' → mostra le aziende collegate; 'company' → mostra le persone collegate */
  mode: 'person' | 'company';
  /** email della persona: abilita il suggerimento auto-associazione per dominio */
  personEmail?: string | null;
  /** Dentro un blocco che si chiama gia' "Persone": niente intestazione doppia. */
  hideTitle?: boolean;
  /** Selettore controllato dal genitore, che tiene il pulsante "Collega" fuori. */
  picking?: boolean;
  onPickingChange?: (v: boolean) => void;
  /**
   * Cambiala per far rileggere i collegamenti: serve a chi salva una persona da
   * fuori (il form "Nuova persona" del pannello lead), altrimenti la nuova riga
   * non compare finche' non si ricarica la pagina.
   */
  versione?: number;
}

const AREA_LABELS: Record<string, string> = {
  amministrazione: 'Amministrazione',
  legale_rappresentante: 'Legale rappr.',
  commerciale: 'Commerciale',
  tecnico: 'Tecnico',
  altro: 'Altro',
};

/**
 * Sezione bidirezionale dei link persona↔azienda (relationship_type='contatto').
 * Scheda Persona → "Aziende collegate"; scheda Azienda → "Persone".
 */
export function ContactCompanyLinksSection({ clientId, mode, personEmail, hideTitle = false, picking: pickingProp, onPickingChange, versione = 0 }: Props) {
  const isPerson = mode === 'person';
  // Se siamo dentro il drawer in-place della Rubrica, ri-targetta lo stesso
  // drawer; altrimenti (pagine Lead/Clienti) fallback alla navigazione classica.
  const { openEntity } = useEntityDrawer();
  const openLinked = (id: string) => {
    if (openEntity) return openEntity(id);
    // Fuori dalla Rubrica: scheda collegata SOPRA quella corrente, come fa la
    // ricerca globale. Prima si navigava a `/clients` con lo stato: la scheda
    // aperta si chiudeva, restava la lista clienti vuota per due secondi e solo
    // dopo compariva quella giusta (misurato l'08/09/2026).
    window.dispatchEvent(new CustomEvent('open-client-modal', { detail: { entityId: id } }));
  };

  // Selettori individuali: referenze stabili (evita loop di re-fetch)
  const fetchForPerson = useContactCompanyLinkStore((s) => s.fetchForPerson);
  const fetchForCompany = useContactCompanyLinkStore((s) => s.fetchForCompany);
  const linkPersonToCompany = useContactCompanyLinkStore((s) => s.linkPersonToCompany);
  const unlink = useContactCompanyLinkStore((s) => s.unlink);
  const updateLink = useContactCompanyLinkStore((s) => s.updateLink);
  const setPrimaryCompany = useContactCompanyLinkStore((s) => s.setPrimaryCompany);
  const setPrimaryContact = useContactCompanyLinkStore((s) => s.setPrimaryContact);

  const [links, setLinks] = useState<ClientRelationship[]>([]);
  const [loading, setLoading] = useState(true);
  const [pickingLocal, setPickingLocal] = useState(false);
  const picking = pickingProp ?? pickingLocal;
  const setPicking = onPickingChange ?? setPickingLocal;
  const [confirmUnlinkId, setConfirmUnlinkId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const data = isPerson ? await fetchForPerson(clientId) : await fetchForCompany(clientId);
    setLinks(data);
    setLoading(false);
  }, [clientId, isPerson, fetchForPerson, fetchForCompany]);

  useEffect(() => { load(); }, [load, versione]);

  // l'entità mostrata nella riga (l'altro lato del link)
  const linkedOf = (l: ClientRelationship) => (isPerson ? (l as any).company : (l as any).person) || {};
  const isPrimary = (l: ClientRelationship) => (isPerson ? l.is_primary_company : l.is_primary_contact);

  const excludeIds = useMemo(
    () => [clientId, ...links.map((l) => linkedOf(l).id).filter(Boolean)],
    [clientId, links, isPerson],
  );

  const handlePick = async (picked: { id: string }) => {
    setPicking(false);
    await linkPersonToCompany({
      person_client_id: isPerson ? clientId : picked.id,
      company_client_id: isPerson ? picked.id : clientId,
    });
    await load();
  };

  const handleSetPrimary = async (l: ClientRelationship) => {
    if (isPerson) await setPrimaryCompany(clientId, l.id);
    else await setPrimaryContact(clientId, l.id);
    await load();
  };

  const handleUnlink = async (l: ClientRelationship) => {
    setConfirmUnlinkId(null);
    await unlink(l.id);
    await load();
  };

  // Etichette: update ottimistico + persist (niente reload → il popover resta aperto)
  const handleLabels = async (l: ClientRelationship, ids: string[]) => {
    setLinks((prev) => prev.map((x) => (x.id === l.id ? { ...x, label_ids: ids } : x)));
    await updateLink(l.id, { label_ids: ids } as Partial<ClientRelationship>);
  };

  // Auto-associazione per dominio email (solo scheda persona): suggerisce
  // l'azienda con lo stesso dominio, se non già collegata. L'utente conferma.
  const [suggested, setSuggested] = useState<DomainCompanyMatch | null>(null);
  const [suggestDismissed, setSuggestDismissed] = useState(false);
  useEffect(() => {
    if (!isPerson || !personEmail || loading || suggestDismissed) { setSuggested(null); return; }
    const domain = extractCompanyDomain(personEmail);
    if (!domain) { setSuggested(null); return; }
    let cancelled = false;
    (async () => {
      const orgId = await getCurrentUserOrganizationId();
      if (!orgId || cancelled) return;
      const match = await findCompanyByDomain(orgId, domain);
      if (cancelled) return;
      const linkedIds = links.map((l) => linkedOf(l).id);
      setSuggested(match && !linkedIds.includes(match.id) ? match : null);
    })();
    return () => { cancelled = true; };
  }, [isPerson, personEmail, loading, links, suggestDismissed]);

  const handleAcceptSuggestion = async () => {
    if (!suggested) return;
    const company = suggested;
    setSuggested(null);
    await linkPersonToCompany({ person_client_id: clientId, company_client_id: company.id });
    await load();
  };

  const title = isPerson ? 'Aziende collegate' : 'Persone';
  const Icon = isPerson ? Building2 : User;
  const primaryLabel = isPerson ? 'Azienda primaria' : 'Referente principale';

  const pickerNode = picking ? (
    <ContactCompanyLinkPicker
      targetEntityType={isPerson ? 'company' : 'individual'}
      excludeIds={excludeIds}
      onPick={handlePick}
      onClose={() => setPicking(false)}
    />
  ) : null;

  return (
    <div className="space-y-1">
      {!hideTitle && (
        <div className="flex items-center justify-between px-1.5">
          <h3 className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-[#787774] dark:text-gray-500">
            <Icon className="h-3 w-3" strokeWidth={1.5} />
            {title}
            {!loading && <span className="font-normal normal-case">({links.length})</span>}
          </h3>
          {!picking && (
            <button
              onClick={() => setPicking(true)}
              className="flex items-center gap-0.5 rounded px-1 py-0.5 text-[11px] text-[#337EA9] hover:bg-[rgba(55,53,47,0.06)] dark:text-[#529CCA] dark:hover:bg-white/[0.06]"
            >
              <Plus className="h-3 w-3" strokeWidth={1.5} /> Collega
            </button>
          )}
        </div>
      )}

      {!hideTitle && pickerNode}

      {/* Suggerimento auto-associazione per dominio email */}
      {suggested && !picking && (
        <div className="flex items-center justify-between gap-2 rounded-md bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-900/30 px-2.5 py-1.5">
          <div className="flex items-center gap-1.5 min-w-0 text-xs text-blue-700 dark:text-blue-300">
            <Building2 className="w-3.5 h-3.5 flex-shrink-0" />
            <span className="truncate">Stesso dominio di <b className="font-semibold">{suggested.business_name}</b></span>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <button onClick={handleAcceptSuggestion} className="text-xs px-2 py-0.5 rounded bg-blue-600 text-white hover:bg-blue-700">Collega</button>
            <button onClick={() => setSuggestDismissed(true)} title="Ignora" className="text-blue-400 hover:text-blue-600"><X className="w-3.5 h-3.5" /></button>
          </div>
        </div>
      )}

      {loading ? (
        hideTitle ? null : (
          <div className="flex items-center gap-2 px-1.5 py-1.5 text-[12px] text-[rgba(55,53,47,0.45)] dark:text-gray-500">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Caricamento…
          </div>
        )
      ) : links.length === 0 ? (
        // Incorporata: il vuoto lo raccontano il riepilogo della sezione e i
        // pulsanti del genitore, una frase in piu' sarebbe rumore.
        hideTitle ? null : (
          <p className="px-1.5 py-1 text-[12px] text-[rgba(55,53,47,0.45)] dark:text-gray-500">
            {isPerson ? 'Nessuna azienda collegata.' : 'Nessuna persona collegata.'}
          </p>
        )
      ) : (
        <ul className="space-y-0.5">
          {links.map((l) => {
            const e = linkedOf(l);
            return (
              <li
                key={l.id}
                className="group/link flex items-start gap-2 rounded px-1.5 py-1.5 transition-colors hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/[0.03]"
              >
                <div className="min-w-0 flex-1">
                  <button
                    onClick={() => e.id && openLinked(e.id)}
                    className="w-full min-w-0 text-left"
                  >
                    <div className="flex items-center gap-1.5">
                      <Icon className="h-3 w-3 flex-shrink-0 text-[#787774] dark:text-gray-500" strokeWidth={1.5} />
                      <span className="truncate text-[13px] font-medium text-[#37352F] dark:text-white">
                        {e.business_name || '—'}
                      </span>
                      {isPrimary(l) && (
                        <span className="flex-shrink-0 rounded bg-[#FBF3DB] px-1.5 py-0.5 text-[10px] text-[#CB912F] dark:bg-[#59563B] dark:text-[#FFDC49]">
                          {isPerson ? 'Primaria' : 'Principale'}
                        </span>
                      )}
                    </div>
                    <div className="ml-[18px] mt-0.5 flex flex-wrap items-center gap-1.5">
                      {l.role && <span className="text-[11px] text-[#787774] dark:text-gray-400">{l.role}</span>}
                      {l.area && (
                        <span className="rounded bg-[rgba(55,53,47,0.08)] px-1.5 py-0.5 text-[10px] text-[#787774] dark:bg-white/[0.08] dark:text-gray-400">
                          {AREA_LABELS[l.area] || l.area}
                        </span>
                      )}
                      {l.is_referente && (
                        <span className="rounded bg-purple-100 px-1.5 py-0.5 text-[10px] text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">Referente</span>
                      )}
                      {l.is_rappresentante_legale && (
                        <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">Rapp. Legale</span>
                      )}
                    </div>
                  </button>
                  <div className="ml-[14px] mt-1" onClick={(ev) => ev.stopPropagation()}>
                    <RelationshipLabelPicker value={l.label_ids || []} onChange={(ids) => handleLabels(l, ids)} />
                  </div>
                </div>
                {confirmUnlinkId === l.id ? (
                  <span className="inline-flex items-center gap-1.5 whitespace-nowrap pt-0.5 text-[11px]">
                    <span className="text-[#787774] dark:text-gray-400">Scollegare?</span>
                    <button
                      onClick={() => handleUnlink(l)}
                      className="rounded bg-red-500 px-2 py-0.5 text-white hover:bg-red-600"
                    >
                      Scollega
                    </button>
                    <button
                      onClick={() => setConfirmUnlinkId(null)}
                      className="rounded bg-[rgba(55,53,47,0.08)] px-2 py-0.5 text-[#787774] hover:bg-[rgba(55,53,47,0.14)] dark:bg-gray-700 dark:text-gray-300"
                    >
                      Annulla
                    </button>
                  </span>
                ) : (
                  <span className="flex flex-shrink-0 items-center gap-1 pt-0.5 opacity-0 transition-opacity group-hover/link:opacity-100">
                    <button
                      onClick={() => handleSetPrimary(l)}
                      title={primaryLabel}
                      className={isPrimary(l) ? 'text-amber-500' : 'text-[rgba(55,53,47,0.3)] hover:text-amber-400 dark:text-gray-600'}
                    >
                      <Star className="h-3.5 w-3.5" fill={isPrimary(l) ? 'currentColor' : 'none'} strokeWidth={1.5} />
                    </button>
                    <button
                      onClick={() => setConfirmUnlinkId(l.id)}
                      title="Scollega"
                      className="text-[rgba(55,53,47,0.3)] hover:text-red-500 dark:text-gray-600"
                    >
                      <X className="h-3.5 w-3.5" strokeWidth={1.5} />
                    </button>
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {hideTitle && pickerNode}
    </div>
  );
}
